import { Box, Divider, Skeleton } from "@mui/material";
import Grid from '@mui/material/Grid2';

import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@common/components/Typography";
import { MenuItems } from "@common/components/CSV/CSVMenu";
import {
  getCurrentSizeProfile,
  responsiveFontSize,
} from "@common/components/Typography/style";
import { LEFT_FIXED_SIZE, getColumns } from "../ColDef";
import {
  FiltersTypeImpl,
  useOtherFilters,
  useUserFilter,
} from "@common/utils/filters";
import { AuditListContext } from "../context";
import FilteringBar from "@common/components/FilteringBar";
import { getAudit } from "@services/audit";
import { mapToCSVData } from "@common/components/CSV/helpers";
import { TabValue } from "@pages/Assets/common/FilterColumn/context";
import mappers from "@common/utils/filters/mappers";
import { AuditProps } from "..";
import { getEntityTypeArray } from "../const";
import { useSpecificListPreferences } from "@common/components/SpecificPreferences/hooks";
import { sortColumnOrder } from "@common/components/SpecificPreferences/utils";

const AuditTrailsFilterBar = ({
  entityType = "",
  accounts,
  entityId,
  userIds,
}: Pick<AuditProps, "accounts" | "entityId" | "userIds"> & {
  entityType: "" | AuditProps["entityType"];
}) => {
  const { t } = useTranslation();
  const { list, total, dispatch, sort } = useContext(AuditListContext);
  const [filters, setFilters] = useOtherFilters("AuditTrails");
  const [auditFilters, setAuditFilters] = useUserFilter("auditFilters");
  const [{ columnOrder }] = useSpecificListPreferences();
  const loading = list === undefined;
  const getDataForCSV = async (item: MenuItems) => {
    const slFilters = mappers.AuditTrails.fromDefinition.toSL(filters);
    const entityTypeArray = getEntityTypeArray(slFilters, entityType);
    const audit = await getAudit(
      {
        ...slFilters,
        accountIds:
          entityType !== "ACCOUNT"
            ? slFilters.accountIds ?? accounts
            : undefined,
        entityType: entityTypeArray,
        entityId: entityId ? [entityId] : undefined,
        userIds: userIds,
      },
      {
        orderBy: `${sort.column} ${sort.direction}`,
      }
    );

    const exportTab =
      item === MenuItems.ListAsDisplayed ? TabValue.Info : TabValue.Export;

    let exportColumns = getColumns(t).filter(
      (c) => c.tab && c.tab.indexOf(exportTab) !== -1
    );
    if (item === MenuItems.ListAsDisplayed) {
      exportColumns = sortColumnOrder(exportColumns, columnOrder);
    }

    const fileName = item === MenuItems.ListAsDisplayed ? "logs" : "logsraw";

    return mapToCSVData(audit.list, exportColumns, fileName, undefined);
  };
  return (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      sx={(theme) => ({
        fontSize: getCurrentSizeProfile().small,
        ...responsiveFontSize(theme, "small"),
        height: "3em",
        py: "0.5em",
        alignItems: "center",
      })}
    >
      <Box
        sx={(theme) => ({
          fontSize: getCurrentSizeProfile().small,
          ...responsiveFontSize(theme, "small"),
          height: "3em",
          padding: "0.5em",
          width: LEFT_FIXED_SIZE,
          minWidth: LEFT_FIXED_SIZE,
        })}
      >
        <Typography
          sx={{
            textAlign: "center",
            lineHeight: 2,
          }}
        >
          {loading ? (
            <Skeleton animation="wave"></Skeleton>
          ) : (
            `${`${total ?? 0}`} ${t(total !== 1 ? "Reports" : "Report")}`
          )}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem />
      <FilteringBar
        hideColumnButton
        filters={filters}
        setFilters={(f) => setFilters([...f])}
        setLocalStorageFilter={setAuditFilters}
        type={FiltersTypeImpl.AuditTrails}
        csvExportProps={{
          getData: getDataForCSV,
          availableMenuItems: [MenuItems.ListAsDisplayed, MenuItems.RawData],
        }}
        loading={loading}
        refetch={() => dispatch({ list: undefined })}
        entity={
          entityType
            ? {
                entityType: getEntityTypeArray(
                  mappers.AuditTrails.fromDefinition.toSL(filters),
                  entityType
                ),
              }
            : undefined
        }
      />
    </Grid>
  );
};

export default AuditTrailsFilterBar;
